import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '@/utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { 
  ArrowLeft, Calendar, Eye, Tag, MessageCircle, Send, 
  Loader2, Package2, User, Check, Share2, Link, Copy, ArrowRight, Clock, X, ChevronLeft, ChevronRight, Images
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import PublicNavbar from '@/components/PublicNavbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPost() {
  const { t, i18n } = useTranslation();
  const { postSlug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const lightboxRef = useRef(null);

  // Category labels with translations
  const getCategoryLabel = (categoryKey) => {
    return t(`blog.category.${categoryKey}`, categoryKey);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen || !post?.gallery_images?.length) return;
    
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setLightboxOpen(false);
          break;
        case 'ArrowLeft':
          setLightboxIndex((prev) => (prev === 0 ? post.gallery_images.length - 1 : prev - 1));
          break;
        case 'ArrowRight':
          setLightboxIndex((prev) => (prev === post.gallery_images.length - 1 ? 0 : prev + 1));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, post?.gallery_images?.length]);
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });

  const loadPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${postSlug}`);
      setPost(response.data);
      loadComments(response.data.post_id);
      loadRelatedPosts(postSlug);
    } catch (error) {
      logger.error('Failed to load post:', error);
      toast.error(t('common.error'));
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  }, [postSlug, navigate, t]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/blog/${postId}/comments`);
      setComments(response.data || []);
    } catch (error) {
      logger.error('Failed to load comments:', error);
    }
  };

  const loadRelatedPosts = async (slug) => {
    try {
      const response = await axios.get(`${API}/blog/${slug}/related`);
      setRelatedPosts(response.data || []);
    } catch (error) {
      logger.error('Failed to load related posts:', error);
    }
  };

  // Set SEO meta tags when post loads
  useEffect(() => {
    if (post) {
      document.title = post.meta_title || `${post.title} | Even Rounds Blog`;
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = post.meta_description || post.excerpt || '';
      
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = post.meta_title || post.title;
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.content = post.meta_description || post.excerpt || '';
      
      if (post.featured_image) {
        let ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.content = post.featured_image;
      }
    }
    
    return () => {
      document.title = 'Even Rounds';
    };
  }, [post]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentForm.author_name.trim() || !commentForm.author_email.trim() || !commentForm.content.trim()) {
      toast.error(t('comments.fillAllFields'));
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/blog/${post.post_id}/comments`, commentForm);
      setCommentSubmitted(true);
      setCommentForm({ author_name: '', author_email: '', content: '' });
      toast.success(t('comments.submitted'));
    } catch (error) {
      logger.error('Failed to submit comment:', error);
      toast.error(t('common.error'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateStr) => {
    const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
    return new Date(dateStr).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // DOMPurify config - preserve all structural HTML tags for formatting
  const PURIFY_CONFIG = {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'span', 'div', 'pre', 'code', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style'],
    KEEP_CONTENT: true,
    // SEC-FIX: Force rel="noopener noreferrer" on all links to prevent reverse-tabnabbing
    ADD_ATTR: ['target'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick']
  };

  // SEC-FIX: Post-process to add rel="noopener noreferrer" to all links with target
  const renderMarkdown = (content) => {
    if (!content) return '';
    let sanitized;
    // If content contains HTML tags, it's from the editor - sanitize with config to preserve formatting
    if (content.includes('<') && content.includes('>')) {
      sanitized = DOMPurify.sanitize(content, PURIFY_CONFIG);
    } else {
      // Otherwise treat as Markdown (legacy content)
      const rawHtml = marked(content);
      sanitized = DOMPurify.sanitize(rawHtml, PURIFY_CONFIG);
    }
    // SEC-FIX: Add rel="noopener noreferrer" to all links with target="_blank"
    sanitized = sanitized.replace(/<a\s+([^>]*target="_blank"[^>]*)>/gi, (match, attrs) => {
      if (!attrs.includes('rel=')) {
        return `<a ${attrs} rel="noopener noreferrer">`;
      }
      return match;
    });
    return sanitized;
  };

  const getShareUrl = () => {
    return window.location.href;
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnInstagram = () => {
    navigator.clipboard.writeText(getShareUrl()).then(() => {
      toast.success(i18n.language === 'fi' 
        ? 'Linkki kopioitu! Avaa Instagram ja liitä tarinaan tai julkaisuun.' 
        : 'Link copied! Open Instagram and paste in your story or post.');
    }).catch(() => {
      toast.error(t('common.error'));
    });
  };

  const shareOnPinterest = () => {
    const image = post?.featured_image || '';
    const description = post ? post.excerpt : '';
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(getShareUrl())}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(description)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success(i18n.language === 'fi' ? 'Linkki kopioitu leikepöydälle!' : 'Link copied to clipboard!');
    } catch (err) {
      toast.error(t('common.error'));
    }
  };

  const calculateReadingTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes < 1 ? 1 : minutes;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNavbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Image */}
        {post.featured_image && (
          <div className="rounded-3xl overflow-hidden mb-8 aspect-video">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Post Header */}
        <header className="mb-8">
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {calculateReadingTime(post.content)} {t('blog.minRead')}
            </span>
            <span className="flex items-center">
              <Eye className="h-4 w-4 mr-1" />
              {post.view_count} {t('blog.views')}
            </span>
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium capitalize">
              {getCategoryLabel(post.category)}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-4">
            {post.title}
          </h1>
          
          <p className="text-xl text-muted-foreground">
            {post.excerpt}
          </p>

          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map(tag => (
                <span
                  key={tag}
                  onClick={() => navigate(`/blog?tag=${encodeURIComponent(tag)}`)}
                  className="flex items-center px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-sm cursor-pointer hover:bg-stone-200 transition-colors"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Social Sharing */}
          <div className="flex items-center gap-4 mt-8 pt-6 border-t border-stone-100">
            <span className="flex items-center text-sm text-muted-foreground font-medium">
              <Share2 className="h-4 w-4 mr-2" />
              {t('blog.share')}:
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={shareOnFacebook}
                data-testid="share-facebook"
                className="h-10 w-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                title="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>
              <button
                onClick={shareOnInstagram}
                data-testid="share-instagram"
                className="h-10 w-10 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                title="Instagram"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </button>
              <button
                onClick={shareOnPinterest}
                data-testid="share-pinterest"
                className="h-10 w-10 rounded-full bg-[#E60023] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                title="Pinterest"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                </svg>
              </button>
              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="h-10 w-10 rounded-full bg-stone-200 text-stone-600 flex items-center justify-center hover:bg-stone-300 transition-colors"
                title={i18n.language === 'fi' ? 'Kopioi linkki' : 'Copy link'}
              >
                <Link className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Post Content */}
        <article className="rounded-3xl bg-white border border-stone-100 p-8 md:p-12 shadow-sm mb-12">
          <div 
            className="prose prose-stone prose-lg max-w-none
              prose-headings:font-heading prose-headings:font-bold
              prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
              prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-3
              prose-h3:text-xl prose-h3:mt-5 prose-h3:mb-2
              prose-p:text-stone-600 prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-stone-800 prose-strong:font-semibold
              prose-em:italic
              prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
              prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
              prose-li:text-stone-600 prose-li:my-1
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-stone-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:italic prose-blockquote:my-4
              prose-img:rounded-xl prose-img:my-6"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
          />

          {/* Image Gallery */}
          {post.gallery_images && post.gallery_images.length > 0 && (
            <div className="mt-10 pt-8 border-t border-stone-100">
              <h3 className="flex items-center text-lg font-heading font-semibold mb-4">
                <Images className="h-5 w-5 mr-2 text-primary" />
                {t('gallery.title')} ({post.gallery_images.length} {post.gallery_images.length === 1 ? t('gallery.image') : t('gallery.images')})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {post.gallery_images.map((imageUrl, idx) => (
                  <button
                    key={imageUrl || idx}
                    onClick={() => {
                      setLightboxIndex(idx);
                      setLightboxOpen(true);
                    }}
                    className="aspect-square rounded-xl overflow-hidden bg-stone-100 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid={`gallery-image-${idx}`}
                  >
                    <img
                      src={imageUrl}
                      alt={`${t('gallery.title')} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold mb-6">
              {t('blog.relatedPosts')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <article
                  key={relatedPost.post_id}
                  onClick={() => navigate(`/blog/${relatedPost.slug || relatedPost.post_id}`)}
                  className="group rounded-2xl bg-white border border-stone-100 overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer"
                >
                  {relatedPost.featured_image ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.featured_image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <Tag className="h-12 w-12 text-primary/30" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <Calendar className="h-3 w-3" />
                      {formatDate(relatedPost.created_at)}
                      <span className="px-2 py-0.5 rounded-full bg-stone-100 capitalize">
                        {getCategoryLabel(relatedPost.category)}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                      {relatedPost.excerpt}
                    </p>
                    <span className="inline-flex items-center text-sm text-primary font-medium mt-3 group-hover:underline">
                      {t('blog.readMore')} <ArrowRight className="h-3 w-3 ml-1" />
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Comments Section */}
        <section className="rounded-3xl bg-white border border-stone-100 p-8 shadow-sm">
          <h2 className="text-2xl font-heading font-bold mb-6 flex items-center">
            <MessageCircle className="h-6 w-6 mr-3 text-primary" />
            {t('comments.title')} ({comments.length})
          </h2>

          {/* Comment List */}
          {comments.length > 0 ? (
            <div className="space-y-6 mb-8">
              {comments.map(comment => (
                <div key={comment.comment_id} className="rounded-2xl bg-stone-50 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{comment.author_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(comment.created_at)}
                        </p>
                      </div>
                    </div>
                    <p className="text-stone-600 pl-13">{comment.content}</p>
                  </div>
                  
                  {/* Admin Reply */}
                  {comment.admin_reply && (
                    <div className="bg-primary/5 border-t border-primary/10 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                          <span className="text-white text-xs font-bold">ER</span>
                        </div>
                        <div>
                          <p className="font-medium text-sm text-primary">Even Rounds</p>
                          {comment.replied_at && (
                            <p className="text-xs text-muted-foreground">
                              {formatDate(comment.replied_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-stone-600 pl-10">{comment.admin_reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground mb-8">
              {t('comments.noComments')}
            </p>
          )}

          {/* Comment Form */}
          {commentSubmitted ? (
            <div className="p-6 rounded-2xl bg-green-50 border border-green-100 text-center">
              <Check className="h-12 w-12 text-green-700 mx-auto mb-3" />
              <h3 className="font-semibold text-green-800 mb-1">{t('comments.thankYou')}</h3>
              <p className="text-sm text-green-700">
                {t('comments.submitted')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleCommentSubmit} className="space-y-4">
              <h3 className="font-semibold text-lg mb-4">{t('comments.leaveComment')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t('comments.name')} *</Label>
                  <Input
                    id="name"
                    value={commentForm.author_name}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, author_name: e.target.value }))}
                    placeholder={t('comments.yourName')}
                    className="mt-1 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t('comments.email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={commentForm.author_email}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, author_email: e.target.value }))}
                    placeholder={t('comments.yourEmail')}
                    className="mt-1 rounded-xl"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('comments.emailPrivate')}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="comment">{t('comments.comment')} *</Label>
                <Textarea
                  id="comment"
                  value={commentForm.content}
                  onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={t('comments.shareThoughts')}
                  className="mt-1 rounded-xl min-h-[120px]"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submittingComment}
                className="rounded-full bg-primary text-primary-foreground"
              >
                {submittingComment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('comments.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('comments.submit')}
                  </>
                )}
              </Button>
            </form>
          )}
        </section>
      </main>

      {/* Lightbox Modal */}
      {lightboxOpen && post.gallery_images && post.gallery_images.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="lightbox-modal"
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid="lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Previous button */}
          {post.gallery_images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev === 0 ? post.gallery_images.length - 1 : prev - 1));
              }}
              className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[85vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={post.gallery_images[lightboxIndex]}
              alt={`${t('gallery.title')} ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              data-testid="lightbox-image"
            />
            <div className="text-center mt-4">
              <p className="text-white/70 text-sm">
                {lightboxIndex + 1} / {post.gallery_images.length}
              </p>
              <p className="text-white/40 text-xs mt-1">
                {t('gallery.navigate')} • {t('gallery.close')}
              </p>
            </div>
          </div>

          {/* Next button */}
          {post.gallery_images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev === post.gallery_images.length - 1 ? 0 : prev + 1));
              }}
              className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="lightbox-next"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Even Rounds. {t('common.madeWithLove')}
          </p>
        </div>
      </footer>
    </div>
  );
}
